import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { SYMBOL_HYPHEN } from '../../../../../../constants/common'
import Button, { BUTTON_HEIGHT_24 } from '../../../../../Button'
import { ICON_HEIGHT_24 } from '../../../../../Icon'
import Text from '../../../../../Text'
import {
  getIsMatchCriterionNotApplicable,
  getMatchCriterionLabelKey,
  getMatchCriterionStatusText,
  getMatchCriterionValueText,
  RentalPortalMatchCriterionChip,
  RentalPortalMatchProvenanceDot,
} from '../../../../RentalPortalMatch'
import { QA_NAME_APPLICANT_FIT_CHECK_EDIT } from '../ApplicantFitCheckBlock.constants'
import {
  ApplicantFitCheckBlockRowEditor,
  getHasMatchEditor,
} from '../ApplicantFitCheckBlockRowEditor'
import { ApplicantFitCheckBlockSuggestion } from '../ApplicantFitCheckBlockSuggestion'

import * as styles from './ApplicantFitCheckBlockRow.module.css'

import EditIcon from '../../../../../../icons/edit.svg'

function ApplicantFitCheckBlockRow({
  criterion,
  currency,
  editValue,
  isEditing,
  isReadOnly,
  isSaving,
  isSubmitting,
  suggestion,
  onAcceptSuggestion,
  onCancelEdit,
  onChangeEditValue,
  onDismissSuggestion,
  onSaveEdit,
  onStartEdit,
}) {
  const { t } = useTranslation()

  const mutedClassName = getIsMatchCriterionNotApplicable(criterion)
    ? styles.muted
    : null

  async function handleAcceptSuggestion() {
    return onAcceptSuggestion(suggestion)
  }

  async function handleDismissSuggestion() {
    return onDismissSuggestion(suggestion)
  }

  async function handleSaveEdit() {
    return onSaveEdit(criterion)
  }

  function handleStartEdit() {
    onStartEdit(criterion)
  }

  return (
    <>
      <Text className={classNames(styles.label, mutedClassName)}>
        {t(getMatchCriterionLabelKey(criterion.key))}
      </Text>
      <Text isSecondary className={classNames(styles.value, mutedClassName)}>
        {getMatchCriterionValueText({
          criterion,
          currency,
          isRequirement: true,
          t,
          value: criterion.listing_value,
        }) ?? SYMBOL_HYPHEN}
      </Text>
      <div className={classNames(styles.applicantValue, mutedClassName)}>
        {isEditing ? (
          <ApplicantFitCheckBlockRowEditor
            criterion={criterion}
            isSaving={isSaving}
            value={editValue}
            onCancel={onCancelEdit}
            onChange={onChangeEditValue}
            onSave={handleSaveEdit}
          />
        ) : (
          <>
            {suggestion && !isReadOnly ? (
              <ApplicantFitCheckBlockSuggestion
                criterion={criterion}
                currency={currency}
                isSubmitting={isSubmitting}
                suggestion={suggestion}
                onAccept={handleAcceptSuggestion}
                onDismiss={handleDismissSuggestion}
              />
            ) : (
              <div className={styles.applicantValue}>
                <RentalPortalMatchProvenanceDot
                  provenance={criterion.provenance}
                />
                <Text isSecondary className={styles.value}>
                  {getMatchCriterionValueText({
                    criterion,
                    currency,
                    t,
                    value: criterion.applicant_value,
                  }) ?? SYMBOL_HYPHEN}
                </Text>
              </div>
            )}
            {!isReadOnly && getHasMatchEditor(criterion) ? (
              <Button.Text
                isGrey
                beforeIconProps={{ Icon: EditIcon, height: ICON_HEIGHT_24 }}
                className={styles.edit}
                height={BUTTON_HEIGHT_24}
                qaName={QA_NAME_APPLICANT_FIT_CHECK_EDIT}
                tooltip={t('RentalPortal.MatchEditKey')}
                onClick={handleStartEdit}
              />
            ) : null}
          </>
        )}
      </div>
      <div className={classNames(styles.chip, mutedClassName)}>
        <RentalPortalMatchCriterionChip
          criterion={criterion}
          text={getMatchCriterionStatusText({ criterion, t })}
        />
      </div>
    </>
  )
}

export default ApplicantFitCheckBlockRow
